// apps/web/src/lib/vendas/pv-da-nota.ts — Fase 12, Plano 27 (PV-04, D-03, D-07).
// Transformações puras do fluxo "PV a partir da NF": rascunho da nota -> itens do formulário.
import type { CriarPedidoVendaItemInput, ItemRascunhoDaNota, MaterialLookupPv } from '@pieltec/contracts';

/** Forma do item do formulário (compatível com `ItemPvForm` do Sheet de item). */
export interface ItemFormDaNota extends CriarPedidoVendaItemInput {
  material: MaterialLookupPv | null;
  precoTabela: number | null;
}

/**
 * Item da NF -> item do formulário para revisão humana (D-03). O preço NÃO vem do `vUnCom` da nota
 * (é o preço de remessa, não o de venda): fica vazio para o servidor precificar pela tabela (D-15);
 * `precoTabela` só alimenta a sinalização de pendência na grade. Sem cProd na nota, o número do item
 * serve de código provisório (o usuário pode editar antes de confirmar).
 */
export function itemFormDoRascunho(item: ItemRascunhoDaNota): ItemFormDaNota {
  const vinculo = item.vinculo;
  const material = vinculo?.vinculado ? vinculo.material : null;
  const codigo = item.codigoItemCliente?.trim();
  return {
    codigoItemCliente: codigo ? codigo : String(item.numeroItem),
    materialId: material?.id ?? null,
    material,
    descricao: item.descricao,
    cor: null,
    quantidade: item.quantidade,
    unidade: item.unidade,
    roteiroId: material ? (vinculo?.roteiroSugerido ?? null) : null,
    precoUnitario: null,
    precoTabela: vinculo?.precoSugerido?.precoUnitario ?? null,
    previsaoEntrega: null,
    documentoFiscalItemId: item.documentoFiscalItemId,
  };
}

/** CNPJ (14 dígitos) ou CPF (11) formatados; qualquer outro valor volta como veio. */
export function formatarDocumentoFiscal(valor: string): string {
  const digitos = valor.replace(/\D/g, '');
  if (digitos.length === 14) return digitos.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  if (digitos.length === 11) return digitos.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  return valor;
}

export function copiaClienteNaoEncontrado(documentoEmitente: string): string {
  return `Não encontramos o cliente pelo CNPJ ${formatarDocumentoFiscal(documentoEmitente)}. Selecione o cliente.`;
}

export const COPIA_NOTA_SEM_ITENS = 'A nota não tem itens. Anexe o XML da nota para criar o PV a partir dos itens.';

export type ModoAcaoPvDaNota = 'ANEXAR_XML' | 'CRIAR' | 'CHIP' | 'NADA';

export interface EntradaModoAcaoPvDaNota {
  readonly temItens: boolean;
  readonly pvsVinculados?: readonly { numero: string }[];
  /** Permissão pv:editar; ausente = o servidor já filtrou (403 esconde tudo). */
  readonly podeEditar?: boolean;
}

/** O que a conferência da NF oferece no rodapé: nota sem itens pede o XML (D-07); com PV mostra o chip; senão criar. */
export function modoAcaoPvDaNota({ temItens, pvsVinculados = [], podeEditar = true }: EntradaModoAcaoPvDaNota): ModoAcaoPvDaNota {
  if (!temItens) return podeEditar ? 'ANEXAR_XML' : 'NADA';
  if (pvsVinculados.length > 0) return 'CHIP';
  return podeEditar ? 'CRIAR' : 'NADA';
}

export type PadraoAnexar =
  | { readonly mostrarCard: false }
  | { readonly mostrarCard: true; readonly modo: 'RADIO'; readonly padrao: 'ANEXAR' }
  | { readonly mostrarCard: true; readonly modo: 'SELECT'; readonly padrao: 'NOVO' };

/** Card "Anexar a PV em aberto": 1 PV -> radio com anexar pré-marcado; vários -> select com "Criar um novo PV" como padrão. */
export function padraoAnexar(quantidadePvsAbertos: number): PadraoAnexar {
  if (quantidadePvsAbertos <= 0) return { mostrarCard: false };
  if (quantidadePvsAbertos === 1) return { mostrarCard: true, modo: 'RADIO', padrao: 'ANEXAR' };
  return { mostrarCard: true, modo: 'SELECT', padrao: 'NOVO' };
}

export function rotuloOpcaoAnexar({ numero, itensAbertos }: { numero: string; itensAbertos: number }): string {
  return `Anexar os itens a ${numero} (${itensAbertos} ${itensAbertos === 1 ? 'item aberto' : 'itens abertos'})`;
}

/** A captura externa só sugere o PV; a remessa/lote/OP dependem do POST físico explícito. */
export function estadoConferenciaDaCaptura(pedidoVendaRascunhoId: string): {
  pedidoVendaRascunhoId: string;
  requerConfirmacaoFisica: true;
} {
  return { pedidoVendaRascunhoId, requerConfirmacaoFisica: true };
}
